import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button } from "antd"

import FormattedValue from "@components/FormattedValue"
import { useAppContext } from "@components/AppContext"
import isSdiraInvestment from "@helpers/isSdiraInvestment"
import { InvestmentShape } from "@shapes"
import { getCurrencyValue } from "@helpers/formats"
import InvestmentProfileCardItem from "@modules/investor/investment/InvestmentFinalizePage/InvestmentProfileCardItem"

import {
  PROJECT_TYPES,
  INVESTMENT_STATUS,
} from "@components/Domain/Constants"
import { W1 } from "@components/constants"
import WireModal from "./WireModal"
import { hasTin } from "./SendFundsButton"
import getSubtitle from "./helpers/getSubtitle"
import FundInvestment from "./FundInvestment"
import NoTinWarningAlert from "./NoTinWarningAlert"
import isCustomerSuspended from "./helpers/isCustomerSuspended"
import useFailedTransaction from "./helpers/useFailedTransaction"
import useInvestmentCustomer from "./helpers/useInvestmentCustomer"
import SdiraFundingAlert from "./SdiraFundingAlert"

const LABEL_WIRE = "View and Download Wiring Instructions"
const LABEL_FAILED = "Failed"
const LABEL_FUNDING = "Send Funding"
const LABEL_OVERFUNDED = "Overfunded"
const LABEL_PARTIALLY_FUNDED = "Partially Funded"
const LABEL_SUBMIT_WIRE_MODAL = "My custodian has confirmed that investment funds are sent"

const InvestmentProfileCardSendFunding = ({ investment, project }) => {
  const [ isWireModalOpen, setIsWireModalOpen ] = useState(false)

  const { getOrganization } = useAppContext()
  const { name: organizationName }  = getOrganization()

  const customer = useInvestmentCustomer(investment)
  const isInvestmentCustomerSuspended = isCustomerSuspended(customer)

  const {
    type: projectType,
    hasBankAccount,
    fundDeadlineDate,
    isAchFundingEnabled,
  } = project

  const isNonFundProject = projectType !== PROJECT_TYPES.FUND

  const isTargetDatePassed =
    isNonFundProject &&
    !!fundDeadlineDate &&
    new Date(fundDeadlineDate).getTime() < Date.now()

  const {
    id: investmentId,
    name: investmentName,
    totalAmount,
    hasSentFunds,
    isFunded,
    isOverfunded,
    isFundingUnlocked,
    isSigned,
    isPartiallySigned,
    isPartiallyFunded,
    status,
    receivedAmount,
    senderSourceId,
    fundsSentAt,
  } = investment

  const isSdira = isSdiraInvestment(investment)

  const isAchFundingDisabled = !isAchFundingEnabled

  const isNonRejected = status !== INVESTMENT_STATUS.REJECTED

  const hasInvestmentTin = hasTin(investment)

  const isPending = hasSentFunds && (!isFunded && !isOverfunded)

  const isComplete = isFunded || isOverfunded

  const isDocumentSigned = isSigned || isPartiallySigned

  const hasNoTinBlock = !hasInvestmentTin && !isFundingUnlocked

  const hasNoSenderSource = !senderSourceId

  const {
    isFailed,
    isInvalidInvestorBankAccount,
    isInsufficientFundsInvestorBankAccount
  } = useFailedTransaction(investment.id)

  const canBeFunded =
    !isPending &&
    !isComplete &&
    isNonRejected &&
    hasBankAccount &&
    isDocumentSigned &&
    !hasNoTinBlock

  const shouldRestrictToWireOnly =
    isSdira ||
    isTargetDatePassed ||
    isAchFundingDisabled ||
    isInvestmentCustomerSuspended

  const shouldRenderFundInvestment = canBeFunded && !shouldRestrictToWireOnly
  const shouldRenderWireInstructions = canBeFunded && shouldRestrictToWireOnly

  const isWireEnabled =
    isDocumentSigned &&
    (hasInvestmentTin || isFundingUnlocked)

  const extraCardProps = () => {
    if (isPartiallyFunded) {
      return { status: LABEL_PARTIALLY_FUNDED }
    }

    if (isOverfunded) {
      return { status: LABEL_OVERFUNDED }
    }

    if (isFailed) {
      return { status: LABEL_FAILED }
    }

    return {}
  }

  const fundingAmount = isPartiallyFunded
    ? `${getCurrencyValue(receivedAmount)} of ${getCurrencyValue(totalAmount)}`
    : <FormattedValue value={totalAmount} />

  const closeWireModal = () =>
    setIsWireModalOpen(false)

  const openWireModal = () =>
    setIsWireModalOpen(true)

  const wireModalFooter = isSdira
    ? <SdiraFundingAlert style={{ marginTop: W1 }} />
    : undefined
  const wireModalSubmitLabel = isSdira ? LABEL_SUBMIT_WIRE_MODAL : undefined

  const wireModalProps = {
    isOpen: isWireModalOpen,
    onClose: closeWireModal,
    totalAmount,
    hasSentFunds,
    investmentId,
    receivedAmount,
    investmentName,
    isPartiallyFunded,
    footer: wireModalFooter,
    submitLabel: wireModalSubmitLabel
  }

  const title = <>{LABEL_FUNDING} ({fundingAmount})</>
  const subtitle = getSubtitle({
    isInvestmentCustomerSuspended,
    isOverfunded,
    isComplete,
    isPartiallyFunded,
    isPending,
    isTargetDatePassed,
    hasBankAccount,
    isSdira,
    shouldRenderWireInstructions,
    hasNoSenderSource,
    fundsSentAt,
    organizationName,
    isInvalidInvestorBankAccount,
    isInsufficientFundsInvestorBankAccount
  })


  const investmentProfileCardProps = {
    title,
    subtitle,
    isFailed,
    isPending,
    isComplete,
    ...extraCardProps()
  }

  return (
    <>
      <InvestmentProfileCardItem {...investmentProfileCardProps}>
        {
          hasNoTinBlock && (
            <NoTinWarningAlert />
          )
        }

        {
          shouldRenderWireInstructions && (
            <Button
              shape="round"
              onClick={openWireModal}
              disabled={!isWireEnabled}
            >
              {LABEL_WIRE}
            </Button>
          )
        }

        {
          shouldRenderFundInvestment && (
            <FundInvestment
              project={project}
              customer={customer}
              investment={investment}
              onOpenFundingModal={openWireModal}
            />
          )
        }
      </InvestmentProfileCardItem>

      <WireModal {...wireModalProps} />
    </>
  )
}

InvestmentProfileCardSendFunding.propTypes = {
  project: PropTypes.shape().isRequired,
  investment: InvestmentShape.isRequired,
}

export default InvestmentProfileCardSendFunding
